import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback
} from 'react';

import { IP } from '../configuracion.js';
import { useProperty } from '../context/PropertyContext';

const LanguageContext = createContext(null);

const DEFAULT_LANGUAGE = 'ES';

// 🔧 CORRECCIÓN: Asegura que la URL base esté correctamente formada
const cleanIP = IP.endsWith('/') ? IP.slice(0, -1) : IP;
const BASE_API_URL = `${cleanIP}/tavpos/backend`;

console.log('🌐 LanguageContext - URL Base:', BASE_API_URL);

export const LanguageProvider = ({ children, currentUser }) => {
  const { property: propertyFromContext } = useProperty();
  const [propertyFromStorage] = useState(null);
  const property = propertyFromContext || propertyFromStorage;

  console.log('🔍 LanguageContext - Property:', property);

  const [language, setLanguage] = useState(DEFAULT_LANGUAGE);
  const [translations, setTranslations] = useState({});
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [networkError, setNetworkError] = useState(false);

  /* ================= FETCH SIMPLE ================= */
  const loadResource = async (filename) => {
    if (!property) {
      console.error('❌ LanguageContext - No property para loadResource');
      throw new Error('Propiedad no definida');
    }

    const url = `${BASE_API_URL}/data/${property}/${filename}`;
    console.log(`📡 LanguageContext - Fetching: ${filename} from:`, url);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    try {
      const response = await fetch(url, {
        signal: controller.signal
      });

      clearTimeout(timeout);

      console.log(`📊 LanguageContext - ${filename} status:`, response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const text = await response.text();
      console.log(`✅ LanguageContext - ${filename} cargado:`, text.length, 'bytes');
      return text;

    } catch (error) {
      clearTimeout(timeout);
      console.error(`🔥 LanguageContext - Error en ${filename}:`, error.message);
      throw error;
    }
  };

  /* ================= LOAD JSON ================= */
  const loadJSON = async (filename) => {
    try {
      const text = await loadResource(filename);
      return JSON.parse(text);
    } catch (error) {
      console.error(`❌ LanguageContext - Error parseando ${filename}:`, error);
      throw error;
    }
  };

  /* ================= LOAD XML ================= */
  const loadXMLTranslations = async (lang) => {
    console.log(`🔍 LanguageContext - Iniciando carga XML para idioma: ${lang}`);
    
    const files = [
      `Idioma_${lang}.xml`,
      `${lang}.xml`,
      `idioma_${lang.toLowerCase()}.xml`
    ];

    console.log('📂 Archivos XML a probar:', files);

    for (const file of files) {
      try {
        console.log(`🔄 LanguageContext - Probando archivo: ${file}`);
        const xmlText = await loadResource(file);
        
        // 🔍 DEBUG: Mostrar contenido del XML
        console.log(`📄 LanguageContext - XML obtenido (${file}):`);
        console.log(xmlText.substring(0, Math.min(1000, xmlText.length)));
        
        const parser = new DOMParser();
        const xml = parser.parseFromString(xmlText, 'text/xml');
        
        const parserError = xml.querySelector('parsererror');
        if (parserError) {
          console.error(`❌ LanguageContext - Error parseando XML:`, parserError.textContent);
          continue;
        }

        // Analizar estructura
        console.log('🏗️ LanguageContext - Estructura XML:');
        console.log('  - Root element:', xml.documentElement.nodeName);
        console.log('  - Child elements:', xml.documentElement.children.length);
        
        // ESTRATEGIAS DE EXTRACCIÓN
        const data = {};
        
        // 1. Buscar elementos con atributo key
        let nodes = xml.querySelectorAll('[key]');
        if (nodes.length > 0) {
          console.log(`✅ LanguageContext - Encontrados ${nodes.length} elementos con key`);
          nodes.forEach(node => {
            const key = node.getAttribute('key');
            const value = node.textContent;
            if (key && value) {
              data[key] = value;
            }
          });
        }
        
        // 2. Si no hay key, buscar por nombre del nodo
        if (nodes.length === 0) {
          const allElements = xml.querySelectorAll('*');
          console.log(`🔍 LanguageContext - Total elementos: ${allElements.length}`);
          
          allElements.forEach(node => {
            // Si es un elemento hoja con texto
            if (node.children.length === 0 && node.textContent.trim()) {
              const key = node.nodeName;
              const value = node.textContent.trim();
              // Excluir elementos estructurales
              if (!['root', 'translations', 'strings', 'idioma', 'language', 'xml'].includes(key.toLowerCase())) {
                data[key] = value;
              }
            }
          });
        }
        
        console.log(`📊 LanguageContext - Extraídas ${Object.keys(data).length} traducciones`);
        
        if (Object.keys(data).length > 0) {
          console.log('✅ LanguageContext - Claves encontradas:', Object.keys(data).slice(0, 5));
          console.log('✅ LanguageContext - we_make_simple:', data['we_make_simple'] || 'NO ENCONTRADA');
          return data;
        }
        
      } catch (e) {
        console.log(`❌ LanguageContext - No se pudo cargar ${file}:`, e.message);
      }
    }

    throw new Error(`No se encontró XML de idioma para ${lang}`);
  };

  /* ================= FALLBACK TRANSLATIONS ================= */
  const getFallbackTranslations = (lang) => {
    const langUpper = lang.toUpperCase();
    
    if (langUpper === 'ES') {
      return {
        login: 'Iniciar Sesión',
        username: 'Usuario',
        password: 'Contraseña',
        submit: 'Enviar',
        we_make_simple: 'Hacemos simple lo complejo',
        welcome: 'Bienvenido',
        error: 'Error',
        loading: 'Cargando...',
        logout: 'Cerrar Sesión',
        settings: 'Configuración',
        user: 'Usuario',
        continue: 'Continuar',
        enter: 'Entrar',
        delete: 'Borrar',
        number: 'Número',
        number_zero: 'Cero',
        min_user_length: 'El usuario debe tener al menos 4 dígitos',
        invalid_credentials: 'Credenciales incorrectas',
        error_loading_employees: 'Error cargando empleados',
        system_error: 'Error del Sistema',
        no_property_selected: 'No se ha seleccionado una propiedad',
        configure_device: 'Configurar Dispositivo',
        retry: 'Reintentar',
        property: 'Propiedad',
        loading_employees: 'Cargando empleados',
        user_field: 'Campo de usuario',
        password_field: 'Campo de contraseña',
        error_loading: 'Error cargando configuración',
        try_again: 'Intentar nuevamente'
      };
    } else {
      return {
        login: 'Login',
        username: 'Username',
        password: 'Password',
        submit: 'Submit',
        we_make_simple: 'We make simple the complex',
        welcome: 'Welcome',
        error: 'Error',
        loading: 'Loading...',
        logout: 'Logout',
        settings: 'Settings',
        user: 'User',
        continue: 'Continue',
        enter: 'Enter',
        delete: 'Delete',
        number: 'Number',
        number_zero: 'Zero',
        min_user_length: 'User must be at least 4 digits',
        invalid_credentials: 'Invalid credentials',
        error_loading_employees: 'Error loading employees',
        system_error: 'System Error',
        no_property_selected: 'No property selected',
        configure_device: 'Configure Device',
        retry: 'Retry',
        property: 'Property',
        loading_employees: 'Loading employees',
        user_field: 'User field',
        password_field: 'Password field',
        error_loading: 'Error loading configuration',
        try_again: 'Try Again'
      };
    }
  };

  /* ================= LOAD LANGUAGE ================= */
  const loadLanguage = useCallback(async (lang) => {
    if (!property) {
      console.warn('⚠️ LanguageContext - No property, omitiendo carga de idioma');
      return;
    }

    const langFinal = (lang || DEFAULT_LANGUAGE).toUpperCase();
    console.log(`🌍 LanguageContext - Cargando idioma: ${langFinal} para property: ${property}`);
    
    setLoading(true);
    setNetworkError(false);

    try {
      const data = await loadXMLTranslations(langFinal);
      console.log(`✅ LanguageContext - Idioma ${langFinal} cargado: ${Object.keys(data).length} traducciones`);
      
      setTranslations(data);
      setLanguage(langFinal);
      setNetworkError(false);
      
    } catch (error) {
      console.error(`❌ LanguageContext - Error cargando idioma ${langFinal}:`, error.message);
      console.log(`🔄 LanguageContext - Usando fallback para ${langFinal}`);
      
      const fallbackTranslations = getFallbackTranslations(langFinal);
      console.log(`🔄 Fallback con ${Object.keys(fallbackTranslations).length} traducciones`);
      
      setTranslations(fallbackTranslations);
      setLanguage(langFinal);
      setNetworkError(true);
    } finally {
      setLoading(false);
    }
  }, [property]);

  /* ================= INIT ================= */
  useEffect(() => {
    if (!property) {
      console.log('⏳ LanguageContext - Esperando property...');
      return;
    }

    console.log('\n🚀 LanguageContext - INICIALIZANDO =================');
    console.log('🏨 Property:', property);

    const init = async () => {
      try {
        console.log('📋 LanguageContext - Cargando config.json...');
        const cfg = await loadJSON('config.json');
        console.log('✅ LanguageContext - Config cargada:', cfg);
        setConfig(cfg);

        // 🔥 EL IDIOMA VIENE DE CONFIG.JSON AUTOMÁTICAMENTE
        let lang = DEFAULT_LANGUAGE;
        if (cfg?.idioma) {
          lang = cfg.idioma.toUpperCase();
          console.log(`🗣️ LanguageContext - Idioma desde config.json: "${lang}"`);
        } else {
          console.warn(`⚠️ LanguageContext - No hay idioma en config.json, usando "${DEFAULT_LANGUAGE}"`);
        }

        console.log(`🎯 LanguageContext - Cargando idioma: ${lang}`);
        await loadLanguage(lang);

      } catch (error) {
        console.error('🔥 LanguageContext - Init falló:', error.message);

        // Configuración de fallback
        setConfig({
          idioma: DEFAULT_LANGUAGE,
          nombre: property,
          fallback: true
        });

        const fallbackTranslations = getFallbackTranslations(DEFAULT_LANGUAGE);
        setTranslations(fallbackTranslations);
        
        setLanguage(DEFAULT_LANGUAGE);
        setNetworkError(true);
        setLoading(false);
      }
    };

    init();
  }, [property, loadLanguage]); // 🔥 Quitamos currentUser de las dependencias si no es necesario

  /* ================= API ================= */
  const t = useCallback(
    (key) => {
      const translation = translations[key];
      return translation || key;
    },
    [translations]
  );

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage: loadLanguage,
        t,
        translations,
        config,
        loading,
        networkError,
        baseUrl: BASE_API_URL,
        currentProperty: property
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage debe usarse dentro de LanguageProvider');
  }
  return ctx;
};